import { authClient } from '@aimfora/auth/client'
import { m } from '@aimfora/i18n'
import { Button, focusFirstInvalid, Notice, toast } from '@aimfora/ui'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { AuthCard, textLinkClass } from '~/components/auth-card'
import { BetaSchoolForm } from '~/components/beta-school-form'
import { FormError, SubmitButton, TextField } from '~/components/form-parts'
import { getPublicConfig, getSessionUser } from '~/functions/session.functions'
import { authErrorMessage } from '~/lib/auth-errors'
import { safeRedirect } from '~/lib/redirect'
import { emailOnlySchema } from '~/lib/schemas'

const RESEND_COOLDOWN_SECONDS = 30

export const Route = createFileRoute('/verify-email')({
  validateSearch: z.object({
    email: z.string().optional(),
    error: z.string().optional(),
    redirect: z.string().optional(),
  }),
  // The verification link sends people here, signed in. Pass them on to where they were going.
  beforeLoad: async ({ search }) => {
    if (await getSessionUser()) throw redirect({ href: safeRedirect(search.redirect) })
  },
  loader: () => getPublicConfig(),
  head: () => ({ meta: [{ title: `${m.verify_title()} – ${m.app_name()}` }] }),
  component: VerifyEmailPage,
})

function VerifyEmailPage() {
  const search = Route.useSearch()
  const config = Route.useLoaderData()
  const router = useRouter()
  const formRef = useRef<HTMLFormElement>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const [email, setEmail] = useState(search.email ?? '')

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const callbackURL = `/verify-email?redirect=${encodeURIComponent(safeRedirect(search.redirect))}`

  const resend = async (address: string) => {
    setServerError(null)
    const { error } = await authClient.sendVerificationEmail({ email: address, callbackURL })
    if (error) {
      setServerError(authErrorMessage(error))
      return
    }
    setCooldown(RESEND_COOLDOWN_SECONDS)
    toast.success(m.verify_resent())
  }

  const form = useForm({
    defaultValues: { email: '' },
    validators: { onBlur: emailOnlySchema(), onSubmit: emailOnlySchema() },
    onSubmitInvalid: () => requestAnimationFrame(() => focusFirstInvalid(formRef.current)),
    onSubmit: async ({ value }) => {
      setEmail(value.email.trim())
      await resend(value.email.trim())
    },
  })

  const linkProblem = Boolean(search.error)

  return (
    <AuthCard
      title={m.verify_title()}
      footer={
        <Link className={textLinkClass} to="/login" search={{ redirect: search.redirect }}>
          {m.verify_to_login()}
        </Link>
      }
    >
      {linkProblem && (
        <Notice tone="attention" role="alert" className="mb-4">
          {m.verify_invalid_token()}
        </Notice>
      )}

      {email ? (
        <div className="space-y-4">
          {!linkProblem && (
            <p className="text-ink-soft text-sm">
              {config.signupMode === 'invite_only'
                ? m.verify_body_invite_only({ email })
                : m.verify_body({ email })}
            </p>
          )}
          <p className="text-ink-soft text-sm">{m.verify_check_spam()}</p>
          {config.signupMode === 'invite_only' && !linkProblem && <BetaSchoolForm email={email} />}
          <FormError message={serverError} />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={cooldown > 0}
            onClick={() => void resend(email)}
          >
            {cooldown > 0 ? `${m.verify_resend()} (${cooldown})` : m.verify_resend()}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => void router.navigate({ to: '/login', search: { email } })}
          >
            {m.auth_login_title()}
          </Button>
        </div>
      ) : (
        <form
          ref={formRef}
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            void form.handleSubmit()
          }}
        >
          <FormError message={serverError} />
          <form.Field name="email">
            {(field) => (
              <TextField
                field={field}
                label={m.auth_email_label()}
                type="email"
                autoComplete="email"
                autoFocus
              />
            )}
          </form.Field>
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(pending) => (
              <SubmitButton pending={pending} className="w-full">
                {m.verify_resend()}
              </SubmitButton>
            )}
          </form.Subscribe>
        </form>
      )}
    </AuthCard>
  )
}
